'use client';

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, XCircle, Loader2, Play, Square, Terminal } from 'lucide-react';

interface TestScenario {
  id: string;
  name: string;
  file: string;
}

interface TestResult {
  scenario: string;
  status: 'pending' | 'running' | 'pass' | 'fail';
  error?: string;
}

interface TestEvent {
  type: 'start' | 'running' | 'pass' | 'fail' | 'complete' | 'output';
  scenario?: string;
  error?: string;
  output?: string;
  total?: number;
  passed?: number;
  failed?: number;
}

export function TestRunner() {
  const [scenarios, setScenarios] = useState<TestScenario[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Map<string, TestResult>>(new Map());
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState<string[]>([]);
  const [summary, setSummary] = useState<{ passed: number; failed: number; total: number } | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load available scenarios
  useEffect(() => {
    fetch('/api/tests/run')
      .then(res => res.json())
      .then(data => {
        if (data.scenarios) {
          setScenarios(data.scenarios);
          // Select all by default
          setSelected(new Set(data.scenarios.map((s: TestScenario) => s.id)));
        }
      })
      .catch(console.error);
  }, []);

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  const toggleScenario = (id: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelected(newSelected);
  };

  const runTests = async () => {
    if (selected.size === 0) return;

    setRunning(true);
    setOutput([]);
    setSummary(null);
    setResults(new Map());

    // Initialize results as pending
    const initialResults = new Map<string, TestResult>();
    selected.forEach(id => {
      const scenario = scenarios.find(s => s.id === id);
      if (scenario) {
        initialResults.set(id, { scenario: scenario.name, status: 'pending' });
      }
    });
    setResults(initialResults);

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch('/api/tests/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenarios: Array.from(selected) }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event: TestEvent = JSON.parse(line.slice(6));
              handleTestEvent(event);
            } catch {
              // Ignore parse errors
            }
          }
        }
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        setOutput(prev => [...prev, `Error: ${(error as Error).message}`]);
      }
    } finally {
      setRunning(false);
      abortControllerRef.current = null;
    }
  };

  const handleTestEvent = (event: TestEvent) => {
    switch (event.type) {
      case 'start':
        setOutput(prev => [...prev, `Starting ${event.total} test scenario(s)...`]);
        break;

      case 'running':
        if (event.scenario) {
          const scenarioName = event.scenario;
          setResults(prev => {
            const newResults = new Map(prev);
            const id = scenarios.find(s => s.name === scenarioName)?.id;
            if (id) {
              newResults.set(id, { scenario: scenarioName, status: 'running' });
            }
            return newResults;
          });
          setOutput(prev => [...prev, `\n▶ Running: ${scenarioName}`]);
        }
        break;

      case 'pass':
        if (event.scenario) {
          const passScenario = event.scenario;
          setResults(prev => {
            const newResults = new Map(prev);
            const id = scenarios.find(s => s.name === passScenario)?.id;
            if (id) {
              newResults.set(id, { scenario: passScenario, status: 'pass' });
            }
            return newResults;
          });
          setOutput(prev => [...prev, `✓ Passed: ${passScenario}`]);
        }
        break;

      case 'fail':
        if (event.scenario) {
          const failScenario = event.scenario;
          const failError = event.error;
          setResults(prev => {
            const newResults = new Map(prev);
            const id = scenarios.find(s => s.name === failScenario)?.id;
            if (id) {
              newResults.set(id, { scenario: failScenario, status: 'fail', error: failError });
            }
            return newResults;
          });
          setOutput(prev => [...prev, `✗ Failed: ${failScenario}`, failError || '']);
        }
        break;

      case 'output':
        if (event.output) {
          setOutput(prev => [...prev, event.output!]);
        }
        break;

      case 'complete':
        setSummary({
          passed: event.passed || 0,
          failed: event.failed || 0,
          total: event.total || 0,
        });
        setOutput(prev => [
          ...prev,
          `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
          `Tests complete: ${event.passed}/${event.total} passed`,
        ]);
        break;
    }
  };

  const stopTests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'pass':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'fail':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <div className="h-4 w-4 rounded-full border-2 border-muted" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span>Integration Tests</span>
            <div className="flex gap-2">
              {running ? (
                <Button variant="destructive" size="sm" onClick={stopTests}>
                  <Square className="h-4 w-4 mr-2" />
                  Stop
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={runTests}
                  disabled={selected.size === 0}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Run Tests
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {scenarios.map(scenario => {
              const result = results.get(scenario.id);
              return (
                <div
                  key={scenario.id}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                    selected.has(scenario.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-muted-foreground/50'
                  }`}
                  onClick={() => !running && toggleScenario(scenario.id)}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selected.has(scenario.id)}
                      onChange={() => toggleScenario(scenario.id)}
                      disabled={running}
                      className="rounded"
                    />
                    <span className="font-medium">{scenario.name}</span>
                    <span className="text-xs text-muted-foreground">{scenario.file}</span>
                  </div>
                  {result && getStatusIcon(result.status)}
                </div>
              );
            })}
          </div>

          {summary && (
            <div className="mt-4 flex gap-2">
              <Badge variant={summary.failed === 0 ? 'default' : 'destructive'}>
                {summary.passed}/{summary.total} passed
              </Badge>
              {summary.failed > 0 && (
                <Badge variant="destructive">{summary.failed} failed</Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Output Console */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Terminal className="h-4 w-4" />
            Test Output
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64 rounded-md border bg-black/90 p-4">
            <div ref={outputRef} className="font-mono text-xs text-green-400 whitespace-pre-wrap">
              {output.length === 0 ? (
                <span className="text-muted-foreground">
                  Select test scenarios and click &quot;Run Tests&quot; to begin...
                </span>
              ) : (
                output.map((line, i) => (
                  <div key={i} className={line.startsWith('✗') ? 'text-red-400' : ''}>
                    {line}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}

